import React, {
  memo,
  ReactElement,
  Children,
  cloneElement,
  CSSProperties,
  MouseEvent,
  useState,
} from 'react'
import sortBy from 'lodash/sortBy'
import { Moment } from 'moment-timezone'
import { Collapse } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { FleetDriver } from 'apps/fleet-planner/components/fleet/FleetDriver'
import { DriverStatusType, IDriver } from 'apps/fleet-planner/types'
import { ICalendarBarProps } from './CalendarBar'

const BAR_CONTAINER_CLASS = 'calendar-view-bars-container'
const BAR_HEIGHT_TOP_OFFSET_PX = 7

const getStyle = (
  [entryStart, entryEnd]: [string, string],
  [rowStart, rowEnd]: [Moment, Moment]
): [CSSProperties, string] => {
  const rangeSize = rowEnd.diff(rowStart, 's')
  const startDiff = -rowStart.diff(entryStart, 's')
  const endDiff = -rowStart.diff(entryEnd, 's')
  const left = (100 * Math.max(0, startDiff)) / rangeSize
  const right = (100 * Math.min(rangeSize, endDiff)) / rangeSize

  const classNames = []
  if (startDiff < 0) classNames.push('overflow-left')
  if (endDiff > rangeSize) classNames.push('overflow-right')

  return [{ left: `${left}%`, width: `${Math.max(1, right - left)}%` }, classNames.join(' ')]
}

const getOverlappingBars = (barStyle: CSSProperties, otherBars: IBar[]): IBar[] => {
  const barLeft = parseFloat((barStyle.left as string) || '0')
  return otherBars.filter(({ props: { style: { left, width } = {} } }) => {
    left = parseFloat((left as string) || '0')
    width = parseFloat((width as string) || '0')
    return barLeft >= left && barLeft < left + width
  })
}

const getTopPosition = (overlaps: IBar[], barHeight: number): number =>
  !overlaps.length
    ? 0
    : barHeight -
      BAR_HEIGHT_TOP_OFFSET_PX +
      overlaps
        .map(({ props: { style: { top } = {} } }) => parseFloat((top as string) || '0'))
        .reduce((max, current) => (current > max ? current : max), 0)

const syncOverlaps = (allBars: IBar[], overlaps: IBar[], height: string) => {
  overlaps.forEach(bar => {
    if (!bar.props.overlapping) {
      allBars[allBars.findIndex(entry => entry === bar)] = cloneElement(bar, {
        overlapping: true,
        style: { ...bar.props.style, height },
      })
    }
  })
}
export interface ICalendarRowProps extends JSX.ElementChildrenAttribute {
  driver: IDriver
  barHeight: number
  overlapBarHeight?: number
  edges?: [Moment, Moment]
  days?: Moment[]
  onClick?: (timestamp: string, context: any) => void
  context?: any
  loading?: boolean
  hasActiveLoads?: boolean
  onToggleDriversNames: () => void
}

interface IBar extends ReactElement<ICalendarBarProps> {}

export const CalendarRow = memo<ICalendarRowProps>(
  ({
    driver,
    edges,
    days,
    barHeight,
    onClick,
    context,
    overlapBarHeight = barHeight,
    children: childrenProp,
    loading,
    hasActiveLoads,
    onToggleDriversNames,
  }) => {
    let rowHeight = barHeight
    const { isMobile } = useBreakpointContext()

    /**
     * If the height of a row is over MAX_HEIGHT,
     * it becomes collapsible and by default it's not expanded
     */
    const MAX_HEIGHT = 140
    const COLLAPSED_HEIGHT = 153
    const [isExpanded, setExpanded] = useState<boolean>(false)

    const toggleRow = () => setExpanded(prev => !prev)

    const bars: IBar[] = []
    const children = loading
      ? []
      : (Children.toArray(childrenProp).filter(Boolean) as ReactElement[])
    sortBy(children, 'props.edges[0]').forEach((bar: IBar) => {
      const { className: initialClassName, edges: entryEdges } = bar.props as ICalendarBarProps
      const [style, className] = getStyle(entryEdges, edges!)
      const overlaps = getOverlappingBars(style, bars)
      const top = getTopPosition(overlaps, overlapBarHeight)
      style.top = `${top}px`
      const overlapping = Boolean(overlaps.length)
      const height = overlapping ? overlapBarHeight : barHeight
      style.height = `${height}px`
      rowHeight = Math.max(rowHeight, top + height)
      syncOverlaps(bars, overlaps, style.height)
      bars.push(
        cloneElement(bar, {
          style,
          className: [className, initialClassName].filter(Boolean).join(' '),
          overlapping,
        })
      )
    })

    const handleKeyDown = () => {}
    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
      const target = event.currentTarget.querySelector<HTMLDivElement>(`.${BAR_CONTAINER_CLASS}`)
      if (edges && onClick && target === event.target) {
        const targetRect = target.getBoundingClientRect()
        const left = (event.pageX - targetRect.left) / targetRect.width
        const timestamp = edges[0]
          .clone()
          .add(Math.max(0, edges[1].diff(edges[0], 's') * left), 's')
          .startOf('h')
          .toISOString()
        onClick(timestamp, context)
      }
    }

    // if driver is deactivated and has no active loads, do not render its row
    if (!hasActiveLoads && driver.status === DriverStatusType.inactive) {
      return null
    }

    const legendColumnClickHandler = (event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation()
      if (onToggleDriversNames) {
        onToggleDriversNames()
      }
    }

    const isRowExpanded = Boolean(isExpanded || isMobile)
    const isRowCollapsed = !isRowExpanded

    return rowHeight > MAX_HEIGHT ? (
      <Collapse
        className={isRowCollapsed ? 'collapsed-calendar-row' : ''}
        in={isRowExpanded}
        timeout='auto'
        collapsedSize={`${COLLAPSED_HEIGHT}px`}>
        <div
          className={`calendar-view-row ${loading ? 'calendar-view-row-loading' : ''}`}
          style={{ minHeight: rowHeight }}
          onKeyDown={handleKeyDown}
          onClick={handleClick}
          role='button'
          tabIndex={0}>
          <div
            className='calendar-view-legend-column'
            aria-hidden='true'
            onClick={legendColumnClickHandler}>
            <FleetDriver
              driver={driver?.id ? driver : undefined}
              isExpanded={isRowExpanded}
              onClick={toggleRow}
              collapsible
            />
          </div>
          <div className={BAR_CONTAINER_CLASS}>
            {Children.map(bars, bar => React.cloneElement(bar, { isExpanded, ...bar }))}
            {!!(loading && days) &&
              days.map(day => <div key={day.valueOf()} className='calendar-view-bar-loading' />)}
          </div>
        </div>
      </Collapse>
    ) : (
      <div
        className={`calendar-view-row ${loading ? 'calendar-view-row-loading' : ''}`}
        style={{ minHeight: rowHeight }}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        role='button'
        tabIndex={0}>
        <div
          className='calendar-view-legend-column'
          aria-hidden='true'
          onClick={legendColumnClickHandler}>
          <FleetDriver
            driver={driver?.id ? driver : undefined}
            isExpanded={isRowExpanded}
            onClick={toggleRow}
          />
        </div>
        <div className={BAR_CONTAINER_CLASS}>
          {Children.map(bars, bar => bar)}
          {!!(loading && days) &&
            days.map(day => <div key={day.valueOf()} className='calendar-view-bar-loading' />)}
        </div>
      </div>
    )
  }
)
